import { useCallback, useEffect, useState } from "react";
import API from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import { Users, Warning } from "@phosphor-icons/react";
import { openAddSeats } from "./AddSeatsDialog";

/** Amber (1 seat left) / red (none left) strip for admins, shown wherever people get invited. */
export default function SeatUsageNudge({ className = "" }) {
  const { user } = useAuth();
  const [info, setInfo] = useState(null);
  const isAdmin = user && ["super_admin", "admin"].includes(user.role) && !user.is_platform_admin;

  const load = useCallback(() => {
    if (!isAdmin) return;
    API.get("/billing/seats/preview?add=1").then(({ data }) => setInfo(data)).catch(() => setInfo(null));
  }, [isAdmin]);

  useEffect(() => {
    load();
    window.addEventListener("fixli:seats-changed", load);
    return () => window.removeEventListener("fixli:seats-changed", load);
  }, [load]);

  if (!isAdmin || !info || info.unlimited || !(info.seats_now > 0)) return null;
  const left = info.seats_now - (info.used ?? 0);
  if (left > 1) return null;
  const full = left <= 0;

  return (
    <div className={`flex flex-wrap items-center gap-3 rounded-md border px-3 py-2 text-xs ${full ? "border-red-300 bg-red-50 text-red-800 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-200" : "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-100"} ${className}`}
      role="status" data-testid="seat-usage-nudge" data-state={full ? "full" : "low"}>
      <Warning size={16} weight="fill" className="shrink-0" />
      <p className="flex-1 min-w-[200px]" data-testid="seat-usage-nudge-text">
        <span className="font-semibold">{info.used} of {info.seats_now} seats in use — {full ? "none left" : "1 left"}.</span>{" "}
        {full ? "The next invite will be blocked until you add a seat." : "Add a seat now so the next invite isn't blocked."}
        {info.per_seat_per_cycle != null && <span className="opacity-80"> £{Number(info.per_seat_per_cycle).toFixed(2)}/seat/{info.cycle === "annual" ? "yr" : "mo"}{info.mode === "trial" ? " after your trial" : ", prorated today"}.</span>}
      </p>
      <Button size="sm" variant={full ? "destructive" : "default"} className="h-7 text-xs" onClick={() => openAddSeats({ add: 1 })} data-testid="seat-usage-nudge-add-btn">
        <Users size={13} className="mr-1" /> Add seats
      </Button>
    </div>
  );
}
